





























































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { useCustomizeFields } from './composables';
import { CustomField } from './types/index';

@Component({
  name: 'CustomizeFieldsPanel'
})
export default class CustomizeFieldsPanel extends Vue {
  @Prop({ type: String, required: true }) readonly pageType!: string;
  @Prop({ type: Array, default: () => [] }) readonly defaultFields!: CustomField[];

  isOpen = false;
  customizeFields: any = null;
  initializedFields = false;
  private showDialog = false;
  private originalFields: Record<string, boolean> = {};
  private scrollTop = 0;
  private itemHeight = 40; // Height of each option in pixels
  private viewportHeight = 400; // Viewport height

  created() {
    this.initializeFields();
  }

  // Watch for prop changes
  @Watch('pageType')
  onPageTypeChange(): void {
    this.resetCustomizer();
  }

  @Watch('defaultFields')
  onDefaultFieldsChanged(newFields: CustomField[], oldFields: CustomField[]): void {
    if (JSON.stringify(newFields) !== JSON.stringify(oldFields)) {
      this.initializeFields();
    }
  }

  get state() {
    return this.customizeFields?.state || {};
  }

  get sortedFields(): CustomField[] {
    return this.customizeFields?.getSortedFields() || [];
  }

  // Calculate which fields should be visible based on scroll position
  get visibleFields(): CustomField[] {
    const allFields = this.sortedFields;
    
    // If there aren't many fields, just return all of them
    if (allFields.length <= 30) {
      return allFields;
    }
    
    // Calculate visible range with buffer
    const buffer = 5; // Additional buffer items above and below
    const startIndex = Math.max(0, Math.floor(this.scrollTop / this.itemHeight) - buffer);
    const endIndex = Math.min(
      allFields.length - 1,
      Math.ceil((this.scrollTop + this.viewportHeight) / this.itemHeight) + buffer
    );
    
    return allFields.slice(startIndex, endIndex + 1);
  }

  get selectedFields(): string[] {
    return this.customizeFields?.getVisibleFields()?.map((field: CustomField) => field.id) || [];
  }

  // Toggle a field's visibility
  toggleField(fieldId: string, isChecked: boolean): void {
    if (this.customizeFields) {
      this.customizeFields.updateFieldVisibility(fieldId, isChecked);
    }
  }

  // Handle scroll events
  handleScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.scrollTop = target.scrollTop;
  }

  // Open dialog and save current state
  openDialog(): void {
    this.showDialog = true;
    // Save original field state for cancellation
    this.originalFields = this.sortedFields.reduce((acc, field) => {
      acc[field.id] = field.visible;
      return acc;
    }, {} as Record<string, boolean>);
    
    // Add scroll event listener
    this.$nextTick(() => {
      const container = this.$el.querySelector('.field-list');
      if (container) {
        container.addEventListener('scroll', this.handleScroll);
      }
    });
  }

  // Cancel changes and restore original state
  handleCancel(): void {
    this.showDialog = false;
    
    // 恢复到打开弹窗前的状态
    Object.entries(this.originalFields).forEach(([id, visible]) => {
      // 使用不保存到本地存储的方式更新字段可见性
      this.customizeFields.updateFieldVisibilityWithoutSave(id, visible);
    });
    
    // Remove scroll event listener
    this.removeScrollListener();
  }

  handleReset(): void {
    if (this.customizeFields) {
      this.customizeFields.resetToDefault(false);
    }
  }

  handleConfirm(): void {
    this.showDialog = false;
    if (!this.customizeFields) {
      return;
    }
    
    // 发送所有字段的可见性状态，包括必填字段
    const allFields = this.sortedFields.map(field => {
      // 确保字段可见性是一个明确的布尔值
      let visible = field.visible;
      
      // 如果可见性为 undefined，则设置为默认值 true
      if (visible === undefined) {
        visible = true;
      }
      
      // 确保必填字段始终可见
      if (field.required) {
        visible = true;
      }
      
      // 特别检查 scheduled 字段
      if (field.id === 'scheduled' && visible === undefined) {
        visible = true;
      }
      
      return {
        id: field.id,
        visible: visible
      };
    });
    
    // 确保 scheduled 字段存在于更新列表中
    const hasScheduledField = allFields.some(field => field.id === 'scheduled');
    if (!hasScheduledField) {
      allFields.push({
        id: 'scheduled',
        visible: true
      });
    }
    
    // 检查是否有 orderStatus 字段
    const hasOrderStatus = allFields.some(field => field.id === 'orderStatus');
    
    // 保存当前设置到本地存储
    try {
      this.customizeFields.saveSettings();
    } catch (error) {
      console.error('[DEBUG-COMPONENT] Error saving settings:', error);
    }
    
    // 发送更新事件
    this.$emit('fields-updated', allFields);
    
    // 移除滚动事件监听器
    this.removeScrollListener();
  }

  handleRetry(): void {
    if (this.customizeFields) {
      this.customizeFields.loadSettings();
    }
  }

  handleDialogClosed(): void {
    // Clean up when dialog is closed
    if (this.state.error) {
      Vue.set(this.state, 'error', null);
    }
    
    // Remove scroll event listener
    this.removeScrollListener();
  }

  // 处理弹窗关闭事件（点击右上角关闭按钮、点击遮罩层或按ESC键）
  handleClose(): void {
    // 设置弹窗不可见
    this.showDialog = false;
    
    // 移除滚动事件监听器
    this.removeScrollListener();
  }

  // Remove scroll listener
  private removeScrollListener(): void {
    const container = this.$el.querySelector('.field-list');
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
    }
  }

  // Reset the customizer
  private resetCustomizer(): void {
    this.customizeFields = useCustomizeFields(this.pageType, this.defaultFields);
  }
  
  // Remove event listener on component destroy
  beforeDestroy(): void {
    this.removeScrollListener();
  }

  // 在弹窗关闭前处理
  beforeClose(done: () => void): void {
    // 恢复到打开弹窗前的状态
    Object.entries(this.originalFields).forEach(([id, visible]) => {
      // 使用不保存到本地存储的方式更新字段可见性
      this.customizeFields.updateFieldVisibilityWithoutSave(id, visible);
    });
    
    // 完成关闭
    done();
  }

  initializeFields(): void {
    this.customizeFields = useCustomizeFields(this.pageType, this.defaultFields);
    this.initializedFields = true;
  }

  mounted() {
    console.log('[DEBUG-COMPONENT] CustomizeFieldsPanel mounted', {
      pageType: this.pageType,
      defaultFields: this.defaultFields
    });
  }

  togglePanel() {
    console.log('[DEBUG-COMPONENT] Toggle panel, current state:', this.isOpen);
    this.isOpen = !this.isOpen;
  }

  toggleFieldVisibility(fieldId: string, visible: boolean) {
    console.log('[DEBUG-COMPONENT] Toggle field visibility', { fieldId, visible });
    if (this.customizeFields) {
      this.customizeFields.updateFieldVisibility(fieldId, visible);
      this.emitFieldsUpdated();
    }
  }

  emitFieldsUpdated() {
    console.log('[DEBUG-COMPONENT] Emitting fields-updated event');
    if (this.customizeFields) {
      const fields = this.customizeFields.getSortedFields();
      console.log('[DEBUG-COMPONENT] Fields to emit:', fields);
      this.$emit('fields-updated', fields);
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/vue-loader/lib/selector.js?type=script&index=0!./src/layouts/shipments-4-shippers/components/CustomizeFieldsPanel/index.vue